import { Link } from 'react-router-dom';
import Header from './home/components/Header';
import HeroSection from './home/components/HeroSection';
import BrandStrip from './home/components/BrandStrip';
import Services from './home/components/Services';
import HowItWorks from './home/components/HowItWorks';
import Testimonials from './home/components/Testimonials';
import WhyChooseUs from './home/components/WhyChooseUs';
import ServiceAreas from './home/components/ServiceAreas';
import QuoteForm from './home/components/QuoteForm';
import Footer from './home/components/Footer';
import FloatingWhatsApp from './home/components/FloatingWhatsApp';
import PageMeta from '../components/PageMeta';
import { siteConfig } from '../config/site';
import { zonasPorSlug } from '../data/zonas';
import { climatizacaoServicesList } from '../data/climatizacaoServices';

/**
 * Home de cada localidade: "Ar Condicionado na Amadora".
 *
 * É a mesma página da home do site — os mesmos componentes, pela mesma ordem —
 * com os textos adaptados à localidade e duas secções extra com o conteúdo
 * local que dá unicidade à página.
 */
const Zona = ({ zonaSlug }: { zonaSlug: string }) => {
  const zona = zonasPorSlug[zonaSlug];

  if (!zona) {
    return (
      <div className="min-h-screen bg-white">
        <PageMeta title="Zona não encontrada" description="A página que procura não existe." path="/zonas" noindex />
        <Header />
        <main className="pt-20 text-center py-20">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Zona não encontrada</h1>
          <Link to="/zonas" className="text-blue-600 hover:text-blue-700">Ver todas as zonas</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const titulo = `Ar Condicionado ${zona.preposicao} ${zona.nome}`;
  const path = `/ar-condicionado-${zona.slug}`;
  const descricao = `Instalação, manutenção e venda de ar condicionado ${zona.preposicao} ${zona.nome}. Técnicos certificados, orçamento gratuito e garantia. ${zona.bairros.slice(0, 4).join(', ')}.`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: `${siteConfig.name} — ${zona.nome}`,
      description: descricao,
      url: `https://www.${siteConfig.domain}${path}`,
      telephone: siteConfig.phone,
      email: siteConfig.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteConfig.address.street,
        addressLocality: siteConfig.address.city,
        postalCode: siteConfig.address.postalCode,
        addressCountry: siteConfig.address.country
      },
      areaServed: [
        { '@type': zona.tipo === 'concelho' ? 'City' : 'Place', name: zona.nome },
        ...zona.bairros.map((b) => ({ '@type': 'Place', name: b }))
      ],
      openingHours: 'Mo-Su 00:00-23:59',
      priceRange: '$$'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: `https://www.${siteConfig.domain}/` },
        { '@type': 'ListItem', position: 2, name: 'Zonas', item: `https://www.${siteConfig.domain}/zonas` },
        { '@type': 'ListItem', position: 3, name: zona.nome, item: `https://www.${siteConfig.domain}${path}` }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={titulo}
        description={descricao}
        path={path}
        keywords={`ar condicionado ${zona.nome}, instalação ar condicionado ${zona.nome}, manutenção ar condicionado ${zona.nome}, técnico ar condicionado ${zona.nome}`}
        jsonLd={jsonLd}
      />
      <Header />
      <HeroSection zona={zona} />
      <BrandStrip />

      {/* Migalhas de pão e abertura específica da localidade */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500 mb-4" aria-label="Navegação">
            <Link to="/" className="hover:text-blue-700">Início</Link>
            <span className="mx-2">/</span>
            <Link to="/zonas" className="hover:text-blue-700">Zonas</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{zona.nome}</span>
          </nav>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">{zona.intro}</p>
        </div>
      </section>

      <Services zona={zona} />

      {/* O edificado e os constrangimentos da localidade */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            O que muda {zona.preposicao} {zona.nome}
          </h2>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-10">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-building-line text-3xl text-blue-600"></i>
                <h3 className="text-xl font-bold text-gray-900">O edificado</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{zona.edificado}</p>
            </div>

            <div className="bg-blue-600 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-alert-line text-3xl text-blue-100"></i>
                <h3 className="text-xl font-bold">O que costuma condicionar</h3>
              </div>
              <ul className="space-y-3">
                {zona.desafios.map((desafio, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-xl text-blue-200 mt-0.5 shrink-0"></i>
                    <span className="text-blue-50 leading-relaxed">{desafio}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Onde atuamos e ligação às páginas de serviço da zona */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Onde atuamos {zona.preposicao} {zona.nome}
            </h3>
            <div className="flex flex-wrap gap-2 mb-8">
              {zona.bairros.map((bairro) => (
                <span key={bairro} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm border border-gray-200">
                  {bairro}
                </span>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Serviços {zona.preposicao} {zona.nome}
            </h3>
            <div className="flex flex-wrap gap-3">
              {climatizacaoServicesList.map((servico) => (
                <Link
                  key={servico.id}
                  to={`/${servico.slug}-${zona.slug}`}
                  className="px-4 py-2 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 text-gray-700 rounded-lg text-sm border border-gray-200 transition-colors"
                >
                  {servico.navTitle}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />

      <div id="avaliacoes">
        <Testimonials />
      </div>

      <WhyChooseUs />

      <section className="py-16 bg-gray-50" id="orcamento">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <QuoteForm />
          </div>
        </div>
      </section>

      <div id="zonas">
        <ServiceAreas />
      </div>

      <FloatingWhatsApp />
      <Footer />
    </div>
  );
};

export default Zona;
